import React, { useState, useEffect, SyntheticEvent, useRef } from "react";
import AdminLayout from "../../layout/AdminLayout";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
  Alert,
  Tabs,
  Tab
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import VideogameAssetIcon from "@mui/icons-material/VideogameAsset";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/tr";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import AdminField from "../../layout/AdminField";
import {
  initializeNewCampaign,
  initializeCampaignData,
} from "../../utils/initializeCampaign";
import { v4 as uuid } from "uuid";
import agent from "../../api/agent";
import { toast } from "react-toastify";
import ImagePickerModal from "../../layout/ImagePickerModal";
import VideoPickerModal from "../../layout/VideoPickerModal";
import Swal from "sweetalert2";
import { basename } from "../../utils/env";
import CampaignFields from "./main/CampaignFields";
import FormCustomization from "./main/FormCustomization";
import ScreenStyleSettings from "./main/ScreenStyleSettings";

export default function MatchWinCampaignDetails() {
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<any | null>(null);
  const [titleInput, setTitleInput] = useState<string>("");
  const [tabValue, setTabValue] = useState(0);
  const [errors, setErrors] = useState<any>({});
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [openVideoModal, setOpenVideoModal] = useState<boolean>(false);
  const [activeVideoField, setActiveVideoField] = useState<string | null>(null);

  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [iframeSize, setIframeSize] = useState({ width: 360, height: 640 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [submitType, setSubmitType] = useState<string | null>(null);

  const formRef = useRef<HTMLFormElement>(null);

  const navigate = useNavigate();
  const gameScreenUrl = `${basename}/game/${id || campaign?.id}?gameType=${campaign?.gameType || 5}`;

  useEffect(() => {
    if (id) {
      agent.Campaigns.details(id).then((response) => {
        const campaignData: any = response;
        campaignData.wheel.slices.sort(
          (a: any, b: any) => a.listOrder - b.listOrder
        );
        setCampaign(initializeCampaignData(campaignData));
        setTitleInput(campaignData.title);
        console.log(campaignData);
      });
    } else {
      const newCampaign = initializeNewCampaign();      
      setCampaign(newCampaign);
      setTitleInput(newCampaign.title);
    }
  }, [id]);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  }, [submitType]);

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleObjectAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newObjectAmount = parseInt(event.target.value);
    const currentObjectAmount = campaign?.matchWin.matchObjects.length || 6;

    if (newObjectAmount > currentObjectAmount) {
      const newObjects = Array(newObjectAmount - currentObjectAmount)
        .fill(null)
        .map((_, index) => ({
          id: uuid(),
          objectValue: "",
          objectImage: "",
          matchWinId: campaign?.matchWin.Id || null,
        }));
      setCampaign({
        ...campaign!,
        matchWin: {
          ...campaign!.matchWin,
          matchObjectAmount: newObjectAmount,
          matchObjects: [...campaign!.matchWin.matchObjects, ...newObjects],
        },
      });
    } else {
      setCampaign({
        ...campaign!,
        matchWin: {
          ...campaign!.matchWin,
          matchObjectAmount: newObjectAmount,
          slices: campaign!.matchWin.matchObjects.slice(0, newObjectAmount),
        },
      });
    }
  };

  const handleObjectChange = (
    index: number,
    field: string,
    value: string | boolean | number | null
  ) => {
    if (!campaign) return;

    setIsDirty(false);

    const newObjects = [...campaign.matchWin.matchObjects];
    newObjects[index] = { ...newObjects[index], [field]: value };
    setCampaign({
      ...campaign,
      matchWin: { ...campaign.matchWin, matchObjects: newObjects },
    });
  };

  const handleFieldChange = (field: string, value: any) => {
    setIsDirty(false);
    setCampaign({ ...campaign, [field]: value });
  };

  const handleScreenStyleChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    setCampaign({
      ...campaign,
      screenStyle: { ...campaign.screenStyle, [field]: value },
    });
  };

  const handleMatchWinChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    const numericNullable = ["gameDuration", "moves"];
    let newVal = value;
    if (numericNullable.includes(field)) {
      // AdminField sends string; turn "" into null, otherwise Number
      if (value === "" || value === undefined || value === null) {
        newVal = null;
      } else {
        const n = Number(value);
        newVal = Number.isNaN(n) ? null : n;
      }
    }

    setCampaign({
      ...campaign,
      matchWin: { ...campaign.matchWin, [field]: newVal },
    });
  };

  const handleFormStateChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    setCampaign({
      ...campaign,
      formState: { ...campaign.formState, [field]: value },
    });
  };

  const validateForm = () => {
    let tempErrors: any = {};

    if (!titleInput) tempErrors.titleInput = "Kampanya Başlığı zorunludur";
    if (campaign?.matchWin.matchObjects.some((matchObject: any) => !matchObject.objectValue)) {
      tempErrors.matchObjects = "Obje ismi obje alanı için zorunludur.";
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isDirty) {
      // If there are no changes, don't resubmit or show error
      if (submitType === "regularSubmit") {
        navigate("/campaigns", { state: { reload: true } });
      }
      return;
    }

    const campaignId = campaign?.id || uuid();
    const screenStyleId = campaign?.screenStyle.id || uuid();
    const matchWinId = campaign?.matchWin.id || uuid();
    const formStateId = campaign?.formState.id || uuid();

    const campaignData = {
      ...campaign,
      id: campaignId,
      gameType: 5,
      title: titleInput,
      startDate: (campaign!.startDate as Dayjs).toISOString(),
      endDate: (campaign!.endDate as Dayjs).toISOString(),
      collectUserData:
          campaign!.collectUserData === 1
            ? 1
            : campaign!.collectUserData === 2
              ? 2
              : campaign!.collectUserData === 3
                ? 3
                : 4,
      screenStyle: {
        ...campaign!.screenStyle,
        id: screenStyleId,
        campaignId: campaignId,
      },
      matchWin: {
        ...campaign!.matchWin,
        id: matchWinId,
        campaignId: campaignId,
        tileAmount:
          campaign!.matchWin.tileAmount === 3
          ? 3
          : campaign!.matchWin.tileAmount === 4
          ? 4
          : 5,
        matchWinType: campaign!.matchWin.matchWinType === 1 ? 1 : 2,
        matchObjects: campaign!.matchWin.matchObjects.map((matchObject: any, index: any) => ({
          ...matchObject,
          id: matchObject.id || uuid(),
          matchWinId: matchWinId
        })),
      },
      formState: {
        ...campaign!.formState,
        id: formStateId,
        campaignId: campaignId,
      },
      wheel: {},
      puzzle: {},
      scratchWin: {},
      touchWin: {},
      quiz: {},
      catchGame: {}
    };

    try {
      if (id) {
        await agent.Campaigns.update(campaignData);
      } else {
        console.log(campaignData);
        await agent.Campaigns.create(campaignData);
      }
      if (submitType === "regularSubmit") {
        navigate("/campaigns", { state: { reload: true } });
      } else {
        setIsDirty(true);
      }
    } catch (error: any) {
      if (error.response) {
        console.error("Error response data:", error.response.data);
      } else {
        console.error("Kampanya düzenlenirken bir hata oluştu:", error.message);
      }
    }
    setSubmitType(null);
  };

  const handleShowGameScreen = async () => {
    if (!validateForm()) {
      toast.error(
        "Lütfen geri dönüp kırmızıyla işaretlenen zorunlu alanları doldurunuz!"
      );
      return;
    }

    setSubmitType("showGameScreen");

    if (!isLoaded) {
      setIframeSrc(gameScreenUrl);
      setIsLoaded(true);
    } else {
      setIframeSrc(null);
      setTimeout(() => setIframeSrc(gameScreenUrl), 100);
    }
  };

  const handleRegularSubmit = async () => {
    if (!validateForm()) {
      toast.error(
        "Lütfen geri dönüp kırmızıyla işaretlenen zorunlu alanları doldurunuz!"
      );
      return;
    }

    setSubmitType("regularSubmit");
  };

  const openImagePicker = (field: string) => {
    setActiveField(field);
    setOpenModal(true);
  };

  const openVideoPicker = (field: string) => {
    setActiveVideoField(field);
    setOpenVideoModal(true);
  };

  const handleImageSelect = (url: string) => {
    if (!campaign || !activeField) return;

    if (activeField.startsWith("object")) {
      const [_, indexStr, objectField] = activeField.split("-");
      const index = parseInt(indexStr, 10);
      handleObjectChange(index, objectField, url);
    } else if (["modalBg", "modalInnerBg"].includes(activeField)) {
      setCampaign({ ...campaign, [activeField]: url });
    } else if (["logo", "screenBg", "screenCenterBg"].includes(activeField)) {
      handleScreenStyleChange(activeField, url);
    } else {
      handleMatchWinChange(activeField, url);
    }

    setOpenModal(false);
  };

  const handleVideoSelect = (url: string) => {
    if (!campaign || !activeVideoField) return;
    setCampaign({ ...campaign, [activeVideoField]: url });
    setOpenVideoModal(false);
  };

  const handleDeleteGameData = () => {
    Swal.fire({
      title: "Silmek istediğinizden emin misiniz?",
      text: "Bu kampanya için kaydedilmiş tüm oyun ve oyuncu verileri silinecektir. Silme işlemini geri alamazsınız!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Tamam",
      cancelButtonText: "İptal",
    }).then((result) => {
      if (result.isConfirmed) {
        agent.Players.deleteAll(id)
          .then(() => {
            toast.success("Tüm oyun ve oyuncu verileri silindi!");
          })
          .catch((error) => {
            toast.error("Veriler silinirken bir hata oluştu.");
          });
      }
    });
  };

  const handleSizeChange = (size: string | number, shouldStartPreview = false) => {
    const parsedSize = Number(size);
    if (!Number.isNaN(parsedSize)) {
      const isLandscape = [1, 3, 5].includes(parsedSize);
      setIframeSize(isLandscape ? { width: 640, height: 360 } : { width: 360, height: 640 });

      if (shouldStartPreview && id) {
        handleShowGameScreen();
      }
      return;
    }

    if (size === "9:16") {
      setIframeSize({ width: 360, height: 640 });
    } else if (size === "16:9") {
      setIframeSize({ width: 640, height: 360 });
    }
  };

  if (!campaign) return null;

  return (
    <AdminLayout title={campaign.title || "Eşle Kazan Kampanyası Ekle"}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/campaigns")}
        sx={{ mr: 3 }}
      >
        Kampanyalara Dön
      </Button>
      {id && (
        <>
          <Button
            variant="contained"
            startIcon={<EmojiEventsIcon />}
            color="secondary"
            onClick={() => navigate(`/scoreboard/${id}?campaignTitle=${campaign.title}&gameType=${campaign.gameType}`)}
            sx={{ mr: 2 }}
          >
            Skor Tablosu
          </Button>
          <Button
            variant="contained"
            startIcon={<AnalyticsIcon />}
            color="success"
            onClick={() => navigate(`/statistics/${id}?campaignTitle=${campaign.title}&gameType=${campaign.gameType}`)}
            sx={{ mr: 2 }}
          >
            İstatistikler
          </Button>
          <Button
            variant="contained"
            startIcon={<VideogameAssetIcon />}
            color="warning"
            onClick={() => window.open(gameScreenUrl, "_blank")}
            sx={{ mr: 2 }}
          >
            Oyun Ekranı
          </Button>
          <Button
            variant="contained"
            startIcon={<DeleteIcon />}
            color="error"
            onClick={handleDeleteGameData}
          >
            Oyun ve Oyuncu Verilerini Sil
          </Button>
        </>
      )}
      {campaign.id ? (
        <Alert sx={{ my: 3 }} severity="warning">
          Kampanyanızı oluşturdunuz. Artık <strong>Eşleme Objesi Alanları</strong>{" "}
          sekmesindeki <strong>Eşleme Objesi Sayısı</strong> oluşturulmuş kampanya
          içinde değiştirilemez.
        </Alert>
      ) : (
        <Alert sx={{ my: 3 }} severity="info">
          Temel kampanya bilgilerinizi girdikten sonra kaydetmeden önce lütfen
          yan sekmelerdeki bilgileri de düzenleyiniz. Dilerseniz{" "}
          <strong>Eşleme Objesi Sayısı</strong> hariç bilgilerinizi sonradan da
          düzenleyebilirsiniz. <strong>Eşleme Objesi Alanları</strong>nda yer alan{" "}
          <strong>Eşleme Objesi Sayısı</strong> kampanya oluşturulduktan sonra
          değiştirilemez. Kampanyalarınızı oluşturduktan sonra ödül
          istatistiklerini inceleyebileceğiniz <strong>İstatistikler</strong>{" "}
          butonu hemen yukarıda yer alacaktır.
        </Alert>
      )}
      <Tabs
        value={tabValue}
        className="admin-tabs"
        onChange={handleTabChange}
        aria-label="basic tabs example"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab
          label="Kampanya Alanları"
          id="simple-tab-0"
          aria-controls="simple-tabpanel-0"
        />
        <Tab
          label="Genel Görselleştirme"
          id="simple-tab-1"
          aria-controls="simple-tabpanel-1"
        />
        <Tab
          label="Eşle Kazan Alanları"
          id="simple-tab-2"
          aria-controls="simple-tabpanel-2"
        />
        <Tab
          label="Eşleme Objesi Alanları"
          id="simple-tab-3"
          aria-controls="simple-tabpanel-3"
        />        
        <Tab
          label="Form Özelleştirme"
          id="simple-tab-4"
          aria-controls="simple-tabpanel-4"
        />
      </Tabs>
      <Divider sx={{ mb: 5 }} />
      <form ref={formRef} onSubmit={handleSubmit}>
        <CampaignFields
          campaign={campaign}
          setCampaign={setCampaign}
          errors={errors}
          setErrors={setErrors}
          titleInput={titleInput}
          setTitleInput={setTitleInput}
          handleFieldChange={handleFieldChange}
          openImagePicker={openImagePicker}
          openVideoPicker={openVideoPicker}
          tabValue={tabValue}
        />
        <ScreenStyleSettings
          tabValue={tabValue}
          tabOrder={1}
          campaign={campaign}
          handleScreenStyleChange={handleScreenStyleChange}
          handleSizeChange={handleSizeChange}
          handleShowGameScreen={handleShowGameScreen}
          openImagePicker={openImagePicker}
          id={id}
          iframeSrc={iframeSrc}
          isLoaded={isLoaded}
          iframeSize={iframeSize}
        />
        {campaign.matchWin && (
          <>
            <div
              role="tabpanel"
              hidden={tabValue !== 2}
              id="simple-tabpanel-2"
              aria-labelledby="simple-tab-2"
            >
              <Typography variant="h5" gutterBottom sx={{ mb: 5 }}>
                Eşle Kazan Ekranı Ayarları
              </Typography>

              <Divider sx={{ mb: 5 }} />
              
              <Grid container spacing={6} sx={{ mb: 4 }}>
                <Grid item xs={6}>
                  <Grid container spacing={4} sx={{ mb: 4 }}>
                    <AdminField
                      label="Kart Görseli"
                      value={campaign.matchWin.coverImage}
                      onChange={(value) =>
                        handleMatchWinChange("coverImage", value)
                      }
                      helperText="Eşleme kartları karesi içinde görsel kullanacaksanız PNG formatında (arkaplansız) ekleyiniz."
                      type="button"
                      buttonText="Galeri'den Seç"
                      onClick={() => openImagePicker("coverImage")}
                      error={!!errors.coverImage}
                      errorMessage={errors.coverImage}
                    />
                    <AdminField
                      label="Kart Rengi"
                      type="color"
                      value={campaign.matchWin.coverColor}
                      onChange={(value) => handleMatchWinChange("coverColor", value)}
                      helperText="Eşleme kartı karelerinin rengini seçiniz"
                    />
                    <AdminField
                      label="Eşle Kazan Oyun Tipi"
                      type="radio"
                      value={campaign.matchWin.matchWinType}
                      onChange={(value) =>
                        handleMatchWinChange("matchWinType", parseInt(value))
                      }
                      helperText="Oyunun süreye veya hamle sayısına bağlı tiplerden hangisi olarak oynanacağını seçiniz"
                      selectOptions={[
                        { label: "Süreye Bağlı", value: 1 },
                        { label: "Hamle Sayısına Bağlı", value: 2 }
                      ]}
                    />
                    <Grid item xs={2}>
                    </Grid>
                    <Grid item xs={10}>
                      <Alert severity="info">
                        <strong>Eşle Kazan Oyun Tipi</strong>'nin <strong>Süreye Bağlı</strong>  olması durumunda aşağıdaki{" "}
                        <strong>Oyun Süresi</strong> alanını boş bırakırsanız <strong>kaybetme senaryosu olmayan</strong> oyun
                        artan süre ile gösterilecektir. Süre girmeniz halinde ise girdiğiniz süreden azalarak oynanacak ve
                        süre bittiğinde oyun kaybedilmiş olacaktır. <strong>Hamle Sayısına Bağlı</strong> oyunda ise
                         <strong>Hamle Sayısı</strong> girilmediğinde toplam hamleyi artan şekilde hesaplayacak, girildiğinde
                        de hamle sayısından azalarak oynanacak oyun hamleler bittiğinde kaybedilmiş olacaktır.
                      </Alert>
                    </Grid>
                    <AdminField
                      label="Oyun Süresi"
                      value={campaign.matchWin.gameDuration ?? ""}
                      type="number"
                      onChange={(value) => handleMatchWinChange("gameDuration", value)}
                      helperText="Eğer oyun süreye bağlı olarak oynanacaksa oyunu kazanmak için kaç saniye verileceğini rakam olarak giriniz"
                    />
                    <AdminField
                      label="Hamle Sayısı"
                      value={campaign.matchWin.moves ?? ""}
                      type="number"
                      onChange={(value) => handleMatchWinChange("moves", value)}
                      helperText="Hamle sayısına bağlı oyunlar için oyunun kaç hamle ile sınırlanacağını rakam olarak buradan giriniz"
                    />
                    <AdminField
                      label="Puan Çarpanı"
                      value={campaign.matchWin.pointMultiplier}
                      type="number"
                      onChange={(value) => handleMatchWinChange("pointMultiplier", value)}
                      helperText="Oyun sonunda kalan hamle sayısı veya süreye göre hesaplanan puan çarpanını rakam olarak giriniz"
                    />
                    <AdminField
                      label="Eşle Kazan Kare Adedi"
                      type="radio"
                      value={campaign.matchWin.tileAmount}
                      onChange={(value) =>
                        handleMatchWinChange("tileAmount", parseInt(value))
                      }
                      helperText="Oyunun kaça kaç karelerden oluşacağını seçiniz"
                      selectOptions={[
                        { label: "3x4", value: 3 },
                        { label: "4x3", value: 4 },
                        { label: "4x4", value: 5 }
                      ]}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </div>
            <div
              role="tabpanel"
              hidden={tabValue !== 3}
              id="simple-tabpanel-3"
              aria-labelledby="simple-tab-3"
            >
              <Grid container spacing={4} sx={{ mb: 4 }}>                
                <AdminField
                  label="Eşleme Objesi Sayısı"
                  type="select"
                  value={campaign.matchWin.matchObjectAmount}
                  onChange={(value) =>
                    handleObjectAmountChange({
                      target: { value: value.toString() },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  disabled={id != null}
                  helperText={
                    id != null
                      ? "Eşleme objesi sayısı kampanya oluşturulduktan sonra değiştirilemez."
                      : "Oyunda kaç çift eşleme objesi olacağını seçiniz"
                  }
                  selectOptions={[
                    { label: "6", value: 6 },
                    { label: "8", value: 8 }
                  ]}
                />

                <Grid item xs={2}>
                  <Typography variant="subtitle2">Eşleme Objeleri</Typography>
                </Grid>
                <Grid item xs={10}>
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Alert sx={{ my: 3 }} severity="info">
                        <strong>Eşleme Objeleri</strong> oyunda çiftler halinde{" "}
                        yer alacaktır. Yani oyun eşleme objesi sayısının iki katı kadar{" "}
                        eşleme kartı ile oynanacaktır.
                        <strong>Örnek:</strong> 6 adet eşlme objesi için 12, 8 adet için{" "}
                        16 kart yer alacaktır.
                      </Alert>
                      {Array.isArray(campaign.matchWin.matchObjects) &&
                        campaign.matchWin.matchObjects.map((matchObject: any, index: any) => (
                          <div key={matchObject.id || index}>
                            <h4>Eşleme Objesi {index + 1}</h4>
                            <Grid container spacing={3}>
                              <AdminField
                                label="Obje Adı"
                                value={matchObject.objectValue}
                                onChange={(value) =>
                                  handleObjectChange(index, "objectValue", value)
                                }
                                error={!!errors.matchObjects}
                                errorMessage={errors.matchObjects}
                              />
                              <AdminField
                                label="Eşleme Objesi Görseli"
                                value={matchObject.objectImage}
                                onChange={(value) =>
                                  handleObjectChange(index, "objectImage", value)
                                }
                                helperText="Eşleme Objesi için görsel ekleyiniz"
                                type="button"
                                buttonText="Galeri'den Seç"
                                onClick={() =>
                                  openImagePicker(`object-${index}-objectImage`)
                                }
                              />
                            </Grid>
                            <Divider sx={{ my: 3 }} />
                          </div>
                        ))}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </div>
            <FormCustomization
              tabValue={tabValue}
              tabOrder={4}
              campaign={campaign}
              handleFormStateChange={handleFormStateChange}
            />
          </>
        )}
        <Divider />
        <Button
          variant="contained"
          color="primary"
          type="button"
          onClick={handleRegularSubmit}
          sx={{ my: 4 }}
        >
          Kaydet
        </Button>
      </form>
      <ImagePickerModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSelect={handleImageSelect}
      />
      <VideoPickerModal
        open={openVideoModal}
        onClose={() => setOpenVideoModal(false)}
        onSelect={handleVideoSelect}
      />
    </AdminLayout>
  );
}
